import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Material3Button } from '../../components/ui/Material3Button';
import PrimaryFrame from '../../components/layout/PrimaryFrame';
import ConditionSummaryStrip, { type ConditionTone, type ConditionStat } from '../../components/ui/ConditionSummaryStrip';
import { useApiLocale } from '../../hooks/useApiLocale';
import { fishingApiService } from '../../services/api';
import { analyticsService } from '../../services/analytics';
import { recommendationCatalogService } from '../../services/recommendationCatalog';
import { useAuthStore } from '../../stores/authStore';
import { useWeatherStore } from '../../stores/weatherStore';
import type { LocationTypeData, PlannerRegion, RigData, SpeciesData, TechniqueData } from '../../types';
import type { FishingAssessment } from '../../types/weather';
import { fallbackRecommendationCatalog, type RecommendationCatalogBundle } from '../recommendations/catalog';
import { DEFAULT_PLANNER_REGION, PLANNER_REGIONS, getCurrentPlannerMonth } from './seasonality';
import { resolveReviewedBeginnerOpportunities, type PlannerOpportunity } from './plannerRecommendationEngine';
import { buildRouteSetupPath } from './routeSetup';
import { usePlannerPreferences } from './plannerPreferences';
import { isStrictBeginnerRoute } from './beginnerRouteQuality';
import { withCurrentLocalePath } from '../../utils/localePath';
import { clearRecentRoute, getRecentRoute, parseRecentRouteIds, type RecentRoute } from './recentRoute';
import {
  coordinatesMatch,
  getRegionRepresentativeCoordinates,
  getStoredHomeLocationState,
  requestUserHomeLocation,
  storeHomeLocationState,
  type HomeLocationState
} from './locationAwareHome';

const formatWindow = (timestamp?: string | null, locale = 'en-GB') => {
  if (!timestamp) return null;
  return new Date(timestamp).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
};

interface HomeConditionLabels {
  wind: string;
  wave: string;
  best: string;
  goodNow: string;
  okNow: string;
  sheltered: string;
}

const buildConditionStrip = (
  assessment: FishingAssessment | null,
  waveHeight: number | null | undefined,
  windSpeed: number | null | undefined,
  labels: HomeConditionLabels,
  locale: string,
  includePreciseWindow: boolean
) => {
  if (!assessment) return null;

  const stats: ConditionStat[] = [];
  if (typeof windSpeed === 'number') stats.push({ label: labels.wind, value: `${Math.round(windSpeed)} m/s` });
  if (typeof waveHeight === 'number') stats.push({ label: labels.wave, value: `${waveHeight.toFixed(1)} m` });
  const windowStart = includePreciseWindow ? formatWindow(assessment.bestWindow?.start || null, locale) : null;
  if (windowStart) stats.push({ label: labels.best, value: windowStart });

  const mobileStats: ConditionStat[] = [];
  if (typeof windSpeed === 'number') mobileStats.push({ label: labels.wind, value: `${Math.round(windSpeed)} m/s` });
  if (typeof waveHeight === 'number') mobileStats.push({ label: labels.wave, value: `${waveHeight.toFixed(1)} m` });
  if (mobileStats.length === 0 && windowStart) mobileStats.push({ label: labels.best, value: windowStart });

  const tone = assessment.verdict as ConditionTone;
  const verdictLabel = tone === 'good' ? labels.goodNow : tone === 'fair' ? labels.okNow : labels.sheltered;

  return { tone, verdictLabel, stats: stats.slice(0, 3), mobileStats: mobileStats.slice(0, 2) };
};

const tString = (value: unknown) => String(value || '');

const translateBeginnerAnchor = (label: string | null | undefined) => label || null;

const HOME_SPECIES_IMAGE_OVERRIDES: Record<string, string> = {
  species_bream_001: '/catalog/optimized/species/species_bream_001-home.jpg',
  species_needlefish_001: '/catalog/optimized/species/species_needlefish_001-home.jpg'
};

const getHomeSpeciesImage = (species: SpeciesData) => HOME_SPECIES_IMAGE_OVERRIDES[species.speciesId] || species.images.primary;

const LoadingPickCard: React.FC = () => (
  <div className="rounded-[18px] border border-outline/80 bg-surface p-3 shadow-sm md:rounded-[20px] md:p-3.5">
    <div className="space-y-3 animate-pulse md:space-y-4">
      <div className="grid grid-cols-[76px_1fr] items-start gap-3 md:grid-cols-1 md:gap-4">
        <div className="h-20 rounded-[18px] bg-surface-variant md:h-56 md:rounded-[20px]" />
        <div className="space-y-1.5">
          <div className="h-3 w-16 rounded-full bg-surface-variant" />
          <div className="h-7 w-2/3 rounded-full bg-surface-variant" />
          <div className="h-4 w-3/4 rounded-full bg-surface-variant" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="h-8 w-20 rounded-full bg-surface-variant" />
        <div className="h-8 w-28 rounded-full bg-surface-variant" />
        <div className="h-8 w-24 rounded-full bg-surface-variant" />
      </div>
      <div className="flex gap-2 md:gap-3">
        <div className="h-10 w-32 rounded-full bg-surface-variant" />
        <div className="h-10 w-24 rounded-full bg-surface-variant" />
      </div>
    </div>
  </div>
);

const HomePlannerPreview: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const apiLocale = useApiLocale();
  const homeT = (key: string, options?: Record<string, unknown>) => tString(t(`homePlanner.${key}`, options));
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { monthIndex, region, accessType, budgetBand } = usePlannerPreferences();
  const currentWeather = useWeatherStore((state) => state.currentWeather);
  const currentPosition = useWeatherStore((state) => state.currentPosition);
  const currentPositionSource = useWeatherStore((state) => state.currentPositionSource);
  const fetchCurrentWeather = useWeatherStore((state) => state.fetchCurrentWeather);
  const updateLocation = useWeatherStore((state) => state.updateLocation);
  const [species, setSpecies] = useState<SpeciesData[]>([]);
  const [techniques, setTechniques] = useState<TechniqueData[]>([]);
  const [rigs, setRigs] = useState<RigData[]>([]);
  const [locations, setLocations] = useState<LocationTypeData[]>([]);
  const [catalogEntries, setCatalogEntries] = useState<RecommendationCatalogBundle[]>(fallbackRecommendationCatalog);
  const [loading, setLoading] = useState(true);
  const [locationState, setLocationState] = useState<HomeLocationState>(() => getStoredHomeLocationState());
  const [locationError, setLocationError] = useState<string | null>(null);
  const [recentRoute, setRecentRoute] = useState<RecentRoute | null>(() => getRecentRoute());

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const [speciesData, techniquesData, rigsData, locationsData, catalogData] = await Promise.all([
          fishingApiService.getSpeciesList({ locale: apiLocale, limit: 80 }).catch(() => []),
          fishingApiService.getTechniquesList({ locale: apiLocale, limit: 80 }).catch(() => []),
          fishingApiService.getRigsList({ locale: apiLocale, limit: 80 }).catch(() => []),
          fishingApiService.getLocationTypesList({ locale: apiLocale, limit: 80 }).catch(() => []),
          recommendationCatalogService.getCatalog(apiLocale).catch(() => fallbackRecommendationCatalog)
        ]);

        if (!active) return;

        setSpecies(speciesData);
        setTechniques(techniquesData);
        setRigs(rigsData);
        setLocations(locationsData);
        setCatalogEntries(catalogData.length > 0 ? catalogData : fallbackRecommendationCatalog);
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [apiLocale]);

  useEffect(() => {
    if (isAuthenticated || locationState.mode === 'locating') return;
    storeHomeLocationState(locationState);
  }, [isAuthenticated, locationState]);

  const anonymousLens = useMemo(
    () => ({
      monthIndex: getCurrentPlannerMonth(),
      region: locationState.region || DEFAULT_PLANNER_REGION,
      accessType: 'shore' as const,
      budgetBand: 'budget' as const
    }),
    [locationState.region]
  );

  const activeLens = isAuthenticated ? { monthIndex, region, accessType, budgetBand } : anonymousLens;

  const homeWeatherTarget = useMemo(() => {
    if (!isAuthenticated) {
      if (!locationState.region) return null;
      return locationState.coordinates || getRegionRepresentativeCoordinates(locationState.region);
    }

    return currentPosition && currentPositionSource === 'device'
      ? currentPosition
      : getRegionRepresentativeCoordinates(activeLens.region);
  }, [activeLens.region, currentPosition, currentPositionSource, isAuthenticated, locationState.coordinates, locationState.region]);

  useEffect(() => {
    if (!homeWeatherTarget) return;

    const targetSource = !isAuthenticated && locationState.source === 'geolocation'
      ? 'device'
      : isAuthenticated && currentPositionSource === 'device'
        ? 'device'
        : 'regional_reference';
    if (
      !currentPosition ||
      currentPosition.lat !== homeWeatherTarget.lat ||
      currentPosition.lon !== homeWeatherTarget.lon ||
      currentPositionSource !== targetSource
    ) {
      updateLocation(homeWeatherTarget.lat, homeWeatherTarget.lon, targetSource);
    }
    void fetchCurrentWeather(homeWeatherTarget.lat, homeWeatherTarget.lon, apiLocale);
  }, [apiLocale, currentPosition, currentPositionSource, fetchCurrentWeather, homeWeatherTarget, isAuthenticated, locationState.source, updateLocation]);

  const plannerResolution = useMemo(
    () => resolveReviewedBeginnerOpportunities(species, techniques, rigs, locations, catalogEntries, activeLens, 1),
    [activeLens, catalogEntries, locations, rigs, species, techniques]
  );

  const monthLabel = homeT(`months.${plannerResolution.effectiveLens.monthIndex}`);
  const regionLabel = homeT(`regions.${plannerResolution.effectiveLens.region}.label`);
  const anonymousSuggestions = !isAuthenticated && locationState.region ? plannerResolution.opportunities : [];
  const signedInFeatured = isAuthenticated ? plannerResolution.opportunities[0] || null : null;
  const recentRouteLabel = useMemo(() => {
    if (!recentRoute) return null;
    const ids = parseRecentRouteIds(recentRoute.routeId);
    if (!ids) return null;

    const recentSpecies = species.find((item) => item.speciesId === ids.speciesId) || null;
    const recentTechnique = techniques.find((item) => item.techniqueId === ids.techniqueId) || null;
    const recentRig = ids.rigId ? rigs.find((item) => item.rigId === ids.rigId) || null : null;
    const recentLocation = ids.locationTypeId
      ? locations.find((item) => item.locationTypeId === ids.locationTypeId) || null
      : null;

    if (!isStrictBeginnerRoute({
      species: recentSpecies,
      technique: recentTechnique,
      rig: recentRig,
      location: recentLocation
    })) {
      return null;
    }

    return `${recentSpecies?.name} • ${recentTechnique?.name}`;
  }, [locations, recentRoute, rigs, species, techniques]);
  const conditionLabels = useMemo<HomeConditionLabels>(() => ({
    wind: homeT('condition.wind'),
    wave: homeT('condition.wave'),
    best: homeT('condition.best'),
    goodNow: homeT('condition.goodNow'),
    okNow: homeT('condition.okNow'),
    sheltered: homeT('condition.sheltered')
  }), [t, apiLocale]);
  const hasExactHomeConditions = (!isAuthenticated && locationState.source === 'geolocation') || (isAuthenticated && currentPositionSource === 'device');
  const currentHomeWeather = currentWeather && coordinatesMatch(currentWeather.location, homeWeatherTarget)
    ? currentWeather
    : null;
  const homeConditions = useMemo(
    () => buildConditionStrip(
      currentHomeWeather?.fishingAssessment || null,
      currentHomeWeather?.marine?.waveHeight,
      currentHomeWeather?.windSpeed,
      conditionLabels,
      apiLocale,
      hasExactHomeConditions
    ),
    [apiLocale, conditionLabels, currentHomeWeather, hasExactHomeConditions]
  );

  const handleUseLocation = async () => {
    setLocationError(null);
    setLocationState({ mode: 'locating', region: locationState.region, source: locationState.source, coordinates: locationState.coordinates });

    analyticsService.trackSelectContent({
      route: '/',
      pageCategory: 'home',
      locale: apiLocale,
      entityType: 'navigation',
      entityId: 'use_location',
      entityName: 'use_location',
      metadata: { source: 'home_location_prompt' }
    });

    try {
      const resolved = await requestUserHomeLocation();
      if (!resolved.region) {
        setLocationState({ mode: 'idle', region: null, source: null, coordinates: null });
        setLocationError(homeT('locationUnavailable'));
        return;
      }

      setLocationState({ mode: 'granted', region: resolved.region, source: 'geolocation', coordinates: resolved.coordinates });
    } catch {
      setLocationState({ mode: 'denied', region: null, source: null, coordinates: null });
      setLocationError(homeT('locationUnavailable'));
    }
  };

  const handlePickRegion = (nextRegion: PlannerRegion) => {
    setLocationError(null);
    setLocationState({ mode: 'manual', region: nextRegion, source: 'manual', coordinates: getRegionRepresentativeCoordinates(nextRegion) });

    analyticsService.trackSelectContent({
      route: '/',
      pageCategory: 'home',
      locale: apiLocale,
      entityType: 'navigation',
      entityId: nextRegion,
      entityName: nextRegion,
      metadata: { source: 'home_region_picker' }
    });
  };

  const handleOpenRouteSetup = (opportunity: PlannerOpportunity) => {
    analyticsService.trackRouteSetupEvent('open_route_setup_from_home', {
      route: '/',
      pageCategory: 'home',
      locale: apiLocale,
      entityType: 'species',
      entityId: opportunity.species.speciesId,
      entityName: opportunity.species.name,
      metadata: {
        destinationRoute: buildRouteSetupPath(opportunity, plannerResolution.effectiveLens),
        source: isAuthenticated ? 'home_signed_in_preview' : 'home_location_preview',
        techniqueId: opportunity.technique.techniqueId,
        rigId: opportunity.rig?.rigId || null,
        locationTypeId: opportunity.location?.locationTypeId || null,
        monthIndex: plannerResolution.effectiveLens.monthIndex,
        region: plannerResolution.effectiveLens.region,
        accessType: plannerResolution.effectiveLens.accessType,
        budgetBand: plannerResolution.effectiveLens.budgetBand
      }
    });

    analyticsService.trackSelectContent({
      route: '/',
      pageCategory: 'home',
      locale: apiLocale,
      entityType: 'species',
      entityId: opportunity.species.speciesId,
      entityName: opportunity.species.name,
      metadata: {
        destinationRoute: buildRouteSetupPath(opportunity, plannerResolution.effectiveLens),
        source: isAuthenticated ? 'home_signed_in_preview' : 'home_location_preview',
        action: 'open_route_setup',
        techniqueId: opportunity.technique.techniqueId,
      rigId: opportunity.rig?.rigId || null,
      locationTypeId: opportunity.location?.locationTypeId || null,
      monthIndex: plannerResolution.effectiveLens.monthIndex,
      region: plannerResolution.effectiveLens.region,
      accessType: plannerResolution.effectiveLens.accessType,
        budgetBand: plannerResolution.effectiveLens.budgetBand
      }
    });

    navigate(buildRouteSetupPath(opportunity, plannerResolution.effectiveLens));
  };

  const handleOpenSpecies = (speciesId: string, speciesName: string, techniqueId: string, rigId?: string | null, locationTypeId?: string | null) => {
    analyticsService.trackRouteSetupEvent('open_species_from_home_pick', {
      route: '/',
      pageCategory: 'home',
      locale: apiLocale,
      entityType: 'species',
      entityId: speciesId,
      entityName: speciesName,
      metadata: {
        destinationRoute: `/codex/species/${speciesId}`,
        source: isAuthenticated ? 'home_signed_in_preview' : 'home_location_preview',
        techniqueId,
        rigId: rigId || null,
        locationTypeId: locationTypeId || null,
        monthIndex: plannerResolution.effectiveLens.monthIndex,
        region: plannerResolution.effectiveLens.region,
        accessType: plannerResolution.effectiveLens.accessType,
        budgetBand: plannerResolution.effectiveLens.budgetBand
      }
    });

    analyticsService.trackSelectContent({
      route: '/',
      pageCategory: 'home',
      locale: apiLocale,
      entityType: 'species',
      entityId: speciesId,
      entityName: speciesName,
      metadata: {
        destinationRoute: `/codex/species/${speciesId}`,
        source: isAuthenticated ? 'home_signed_in_preview' : 'home_location_preview',
        techniqueId,
        rigId: rigId || null,
      locationTypeId: locationTypeId || null,
      monthIndex: plannerResolution.effectiveLens.monthIndex,
      region: plannerResolution.effectiveLens.region,
      accessType: plannerResolution.effectiveLens.accessType,
        budgetBand: plannerResolution.effectiveLens.budgetBand
      }
    });

    navigate(withCurrentLocalePath(`/codex/species/${speciesId}`));
  };

  const handleOpenBeginnerGuide = (source: string) => {
    analyticsService.trackSelectContent({
      route: '/',
      pageCategory: 'home',
      locale: apiLocale,
      entityType: 'navigation',
      entityId: 'onboarding',
      entityName: 'onboarding',
      metadata: { source }
    });

    navigate(withCurrentLocalePath('/onboarding'));
  };

  const handleClearRecentRoute = () => {
    clearRecentRoute();
    setRecentRoute(null);
  };

  if (!isAuthenticated) {
    return (
      <PrimaryFrame
        eyebrow={homeT('eyebrow')}
        title={homeT('anonymousTitle')}
        className="overflow-hidden"
        bodyClassName="space-y-3 p-4 md:space-y-4 md:p-5"
      >

          {recentRoute && recentRouteLabel ? (
            <div className="rounded-[20px] border border-primary-200 bg-[#caf0f8]/30 p-4 shadow-sm md:rounded-[24px] md:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1.5">
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-primary-600">{homeT('recentRouteEyebrow')}</div>
                  <h2 className="text-xl font-semibold text-on-surface">{homeT('recentRouteTitle')}</h2>
                  <p className="text-sm text-on-surface-variant">
                    {homeT('recentRouteBody', { route: recentRouteLabel })}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Material3Button size="sm" variant="filled" onClick={() => navigate(withCurrentLocalePath(recentRoute.path))}>
                    {homeT('continueRoute')}
                  </Material3Button>
                  <Material3Button size="sm" variant="text" onClick={handleClearRecentRoute}>
                    {homeT('clearRecentRoute')}
                  </Material3Button>
                </div>
              </div>
            </div>
          ) : null}

          {!locationState.region ? (
            <div className="space-y-4 rounded-[20px] border border-outline/80 bg-white p-4 shadow-sm md:space-y-4 md:rounded-[24px] md:p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold text-on-surface md:text-2xl">{homeT('locationPromptTitle')}</h2>
                  <p className="brand-copy-sm">{homeT('locationPromptBody')}</p>
                </div>
                <div className="flex flex-wrap gap-2 md:gap-3">
                  <Material3Button size="sm" variant="filled" onClick={handleUseLocation} disabled={locationState.mode === 'locating'}>
                    {locationState.mode === 'locating' ? homeT('locating') : homeT('useLocation')}
                  </Material3Button>
                </div>
              </div>

              {locationError && <p className="text-sm font-medium text-primary-700">{locationError}</p>}

              <div className="rounded-[18px] border border-primary-200 bg-[#caf0f8]/30 px-4 py-3.5 md:rounded-[20px] md:px-4 md:py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1.5">
                    <div className="text-sm font-semibold text-on-surface">{homeT('helpTitle')}</div>
                    <p className="text-sm text-on-surface-variant">{homeT('helpBodyPrompt')}</p>
                  </div>
                  <Material3Button size="sm" variant="outlined" onClick={() => handleOpenBeginnerGuide('home_location_prompt_help')}>
                    {homeT('startHere')}
                  </Material3Button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-primary-600">{homeT('pickAreaInstead')}</div>
                <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 md:gap-3">
                  {PLANNER_REGIONS.map((regionOption) => (
                    <button
                      key={regionOption.value}
                      type="button"
                      onClick={() => handlePickRegion(regionOption.value)}
                      className="rounded-[18px] border border-outline/70 bg-surface px-3 py-3 text-left transition-colors hover:bg-surface-variant md:rounded-[22px] md:px-4 md:py-4"
                    >
                      <div className="text-sm font-semibold text-on-surface">{homeT(`regions.${regionOption.value}.label`)}</div>
                      <div className="mt-1 text-xs text-on-surface-variant">{homeT(`regions.${regionOption.value}.shortLabel`)}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3 rounded-[20px] border border-outline/80 bg-white p-4 shadow-sm md:space-y-4 md:rounded-[24px] md:p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-600 md:text-sm md:normal-case md:tracking-normal">{monthLabel} • {regionLabel}</div>
                  <h2 className="text-[1.4rem] font-semibold leading-[1.05] text-on-surface md:text-[1.8rem]">{homeT('bestShorePicks')}</h2>
                  <div className="text-sm font-medium text-primary-600">{homeT('shoreFirst')}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Material3Button size="sm" variant="text" onClick={() => setLocationState({ mode: 'idle', region: null, source: null, coordinates: null })}>
                    {homeT('changeArea')}
                  </Material3Button>
                </div>
              </div>

              <div className="rounded-[18px] border border-primary-200 bg-[#caf0f8]/35 px-4 py-3.5 md:rounded-[20px] md:px-4 md:py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1.5">
                    <div className="text-sm font-semibold text-on-surface">{homeT('helpTitle')}</div>
                    <p className="text-sm text-on-surface-variant">{homeT('helpBodyPicks')}</p>
                  </div>
                  <Material3Button size="sm" variant="outlined" onClick={() => handleOpenBeginnerGuide('home_location_picks_help')}>
                    {homeT('startHere')}
                  </Material3Button>
                </div>
              </div>

              {homeConditions && (
                <div className="space-y-2">
                  <ConditionSummaryStrip
                    eyebrow={homeT(hasExactHomeConditions ? 'conditionsAtLocation' : 'regionalConditions')}
                    tone={homeConditions.tone}
                    verdictLabel={homeConditions.verdictLabel}
                    stats={homeConditions.mobileStats?.length ? homeConditions.mobileStats : homeConditions.stats}
                    actionLabel={homeT('fullConditions')}
                    onAction={() => navigate(withCurrentLocalePath('/toolbox?geo=1'))}
                    compact
                  />
                  {!hasExactHomeConditions && <p className="text-xs leading-5 text-on-surface-variant">{homeT('regionalConditionsBody')}</p>}
                </div>
              )}

              {loading ? (
                <div className="max-w-2xl">
                  <LoadingPickCard />
                </div>
              ) : anonymousSuggestions.length > 0 ? (
                <div className="max-w-2xl">
                  {anonymousSuggestions.map((opportunity, index) => (
                    <div key={opportunity.id} className="rounded-[18px] border border-outline/80 bg-surface p-3 shadow-sm md:rounded-[20px] md:p-3.5">
                      <div className="space-y-3 md:space-y-4">
                        <div className="grid grid-cols-[76px_1fr] items-start gap-3 md:grid-cols-1 md:gap-4">
                          <div className="self-start overflow-hidden rounded-[18px] border border-outline/70 bg-surface-variant md:rounded-[20px]">
                            <img
                              src={getHomeSpeciesImage(opportunity.species)}
                              alt={opportunity.species.name}
                              className="h-20 w-full object-cover md:h-44"
                              width={760}
                              height={439}
                              loading={index === 0 ? 'eager' : 'lazy'}
                              decoding="async"
                              fetchPriority={index === 0 ? 'high' : 'low'}
                            />
                          </div>

                          <div className="min-w-0 space-y-2">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary-600">{index === 0 ? homeT('bestNow') : homeT('alsoGood')}</div>
                            <h3 className="text-[1.4rem] font-semibold leading-[1.05] text-on-surface md:text-[1.8rem]">{opportunity.species.name}</h3>
                            <p className="text-sm leading-snug text-on-surface-variant">{homeT('homeHint', { spot: opportunity.location?.name || homeT('shore') })}</p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-1.5 md:gap-2">
                          <span className="hidden md:inline-flex brand-chip-quiet">{homeT('shore')}</span>
                          <span className="brand-chip-quiet">{opportunity.technique.name}</span>
                          <span className="brand-chip-quiet">{opportunity.rig?.name || homeT('simpleSetup')}</span>
                          {translateBeginnerAnchor(opportunity.beginnerAnchorLabel) ? <span className="brand-chip">{translateBeginnerAnchor(opportunity.beginnerAnchorLabel)}</span> : null}
                        </div>

                        <div className="flex flex-wrap gap-2 md:gap-3">
                          <Material3Button size="sm" variant="filled" onClick={() => handleOpenRouteSetup(opportunity)}>
                            {homeT('seeSetup')}
                          </Material3Button>
                          <Material3Button size="sm" variant="text" onClick={() => handleOpenSpecies(opportunity.species.speciesId, opportunity.species.name, opportunity.technique.techniqueId, opportunity.rig?.rigId, opportunity.location?.locationTypeId)}>
                            {homeT('openFish')}
                          </Material3Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="brand-empty">{homeT('emptyPicks')}</div>
              )}
            </div>
          )}
      </PrimaryFrame>
    );
  }

  return (
    <PrimaryFrame
      eyebrow={homeT('eyebrow')}
      title={homeT('signedInTitle')}
      subtitle={homeT('signedInSubtitle')}
      className="overflow-hidden"
      bodyClassName="space-y-5 p-6 md:p-8"
    >

        {homeConditions && (
          <div className="space-y-2">
            <ConditionSummaryStrip
              eyebrow={homeT(hasExactHomeConditions ? 'conditionsAtLocation' : 'regionalConditions')}
              tone={homeConditions.tone}
              verdictLabel={homeConditions.verdictLabel}
              stats={homeConditions.mobileStats?.length ? homeConditions.mobileStats : homeConditions.stats}
              actionLabel={homeT('fullConditions')}
              onAction={() => navigate(withCurrentLocalePath('/toolbox?geo=1'))}
              compact
            />
            {!hasExactHomeConditions && <p className="text-xs leading-5 text-on-surface-variant">{homeT('regionalConditionsBody')}</p>}
          </div>
        )}

        {signedInFeatured ? (
          <div className="space-y-4 rounded-[28px] border border-outline/80 bg-white p-5 shadow-elevation-1">
            <div className="grid gap-5 lg:grid-cols-[280px_1fr] lg:items-center">
              <div className="overflow-hidden rounded-[24px] border border-outline/70 bg-surface-variant">
                <img
                  src={getHomeSpeciesImage(signedInFeatured.species)}
                  alt={signedInFeatured.species.name}
                  className="h-56 w-full object-cover lg:h-64"
                  width={760}
                  height={439}
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                />
              </div>

              <div className="space-y-3 md:space-y-4">
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-primary-600">{monthLabel} • {homeT(`regions.${plannerResolution.effectiveLens.region}.label`)}</div>
                  <h2 className="text-3xl font-semibold text-on-surface">{signedInFeatured.species.name}</h2>
                  <p className="brand-copy-sm">{homeT('homeHint', { spot: signedInFeatured.location?.name || homeT('shore') })}</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="brand-chip-quiet">{homeT(`access.${plannerResolution.effectiveLens.accessType}`)}</span>
                  <span className="brand-chip-quiet">{signedInFeatured.technique.name}</span>
                  <span className="brand-chip-quiet">{signedInFeatured.rig?.name || homeT('simpleSetup')}</span>
                  {translateBeginnerAnchor(signedInFeatured.beginnerAnchorLabel) ? <span className="brand-chip">{translateBeginnerAnchor(signedInFeatured.beginnerAnchorLabel)}</span> : null}
                </div>

                <div className="flex flex-wrap gap-2 md:gap-3">
                  <Material3Button variant="filled" onClick={() => handleOpenRouteSetup(signedInFeatured)}>
                    {homeT('seeSetup')}
                  </Material3Button>
                  <Material3Button variant="text" onClick={() => handleOpenSpecies(signedInFeatured.species.speciesId, signedInFeatured.species.name, signedInFeatured.technique.techniqueId, signedInFeatured.rig?.rigId, signedInFeatured.location?.locationTypeId)}>
                    {homeT('openFish')}
                  </Material3Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-[24px] border border-outline/80 bg-white p-5 shadow-sm">
            <div className="animate-pulse space-y-4">
              <div className="h-5 w-24 rounded-full bg-surface-variant" />
              <div className="h-8 w-2/3 rounded-full bg-surface-variant" />
              <div className="grid grid-cols-3 gap-3">
                <div className="h-20 rounded-[22px] bg-surface-variant" />
                <div className="h-20 rounded-[22px] bg-surface-variant" />
                <div className="h-20 rounded-[22px] bg-surface-variant" />
              </div>
            </div>
          </div>
        )}
    </PrimaryFrame>
  );
};

export default HomePlannerPreview;
